import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { t } from 'i18next';

import { ExportColumn } from '~utils/export/exportColumn';

export interface ExportTablePdfHeaderMeta {
  companyLogoBase64?: string;
  companyName: string;
  filterDateLabel?: string;
  generatedAt: string;
  treadLogoBase64?: string;
}

interface ExportTablePdfProps<T> {
  columns: ExportColumn<T>[];
  headerMeta: ExportTablePdfHeaderMeta;
  rows: T[];
  title: string;
}

const A4_LANDSCAPE_WIDTH_PT = 841.89;
const A4_LANDSCAPE_HEIGHT_PT = 595.28;
const PAGE_HORIZONTAL_PADDING_PT = 20;
// Baseline content width = standard A4 landscape. The page grows WIDER than this when
// many columns are selected so each column stays legible (a printer can scale-to-fit).
const A4_CONTENT_WIDTH_PT = A4_LANDSCAPE_WIDTH_PT - PAGE_HORIZONTAL_PADDING_PT * 2;
// Minimum legible column width — enough for the longest single header word (~9 chars)
// at the table font size. Drives how wide the page becomes as columns are added.
const MIN_PDF_COLUMN_WIDTH_PT = 46;
const DEFAULT_PDF_COLUMN_WEIGHT = 10;
// Long unbroken tokens (IDs like "R-P6C.1-178386-8355116") must be able to wrap
// inside a narrow column instead of overflowing into the neighbor. We insert
// zero-width spaces as break opportunities every few chars into long runs — a
// local transform, so we avoid a global `Font.registerHyphenationCallback` that
// would change wrapping for every other PDF (quotes, job cards) in the session.
const LONG_TOKEN_BREAK_THRESHOLD = 10;
const LONG_TOKEN_CHUNK_LENGTH = 6;
const ZERO_WIDTH_SPACE = '​';

function withSoftBreaks(text: string) {
  return text.replace(new RegExp(`\\S{${LONG_TOKEN_BREAK_THRESHOLD},}`, 'g'), (run) =>
    run.replace(
      new RegExp(`(.{${LONG_TOKEN_CHUNK_LENGTH}})`, 'g'),
      `$1${ZERO_WIDTH_SPACE}`,
    ),
  );
}

const styles = StyleSheet.create({
  page: {
    fontSize: 6.5,
    // Footer is absolute-bottom; reserve room so table rows never collide with it.
    paddingBottom: 44,
    paddingHorizontal: PAGE_HORIZONTAL_PADDING_PT,
    paddingTop: PAGE_HORIZONTAL_PADDING_PT,
  },
  reportHeader: {
    alignItems: 'center',
    borderBottomColor: '#d1d5db',
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    minHeight: 40,
    paddingBottom: 8,
  },
  companyLogo: {
    height: 22,
    objectFit: 'contain',
    objectPosition: 'left center',
    width: 110,
  },
  companyName: {
    color: '#374151',
    fontSize: 10.5,
    fontWeight: 'bold',
  },
  reportHeaderMeta: {
    alignItems: 'flex-end',
    maxWidth: 360,
  },
  title: {
    color: '#111827',
    fontSize: 11.5,
    fontWeight: 'bold',
    marginBottom: 3,
    textAlign: 'right',
  },
  metaText: {
    color: '#4b5563',
    fontSize: 7,
    marginTop: 1,
    textAlign: 'right',
  },
  table: {},
  tableHeaderRow: {
    backgroundColor: '#374151',
    borderColor: '#374151',
    borderWidth: 1,
    flexDirection: 'row',
    minHeight: 28,
  },
  tr: {
    borderBottomColor: '#d1d5db',
    borderBottomWidth: 1,
    borderLeftColor: '#d1d5db',
    borderLeftWidth: 1,
    flexDirection: 'row',
  },
  trAlt: {
    backgroundColor: '#f9fafb',
  },
  th: {
    color: '#ffffff',
    fontSize: 6.5,
    fontWeight: 'bold',
    overflow: 'hidden',
    paddingHorizontal: 2,
    paddingVertical: 4,
  },
  td: {
    borderRightColor: '#d1d5db',
    borderRightWidth: 1,
    color: '#1f2937',
    fontSize: 6.5,
    lineHeight: 1.25,
    minHeight: 18,
    overflow: 'hidden',
    paddingHorizontal: 2,
    paddingVertical: 3,
  },
  footer: {
    alignItems: 'center',
    borderTopColor: '#d1d5db',
    borderTopWidth: 1,
    bottom: 18,
    flexDirection: 'row',
    justifyContent: 'space-between',
    left: PAGE_HORIZONTAL_PADDING_PT,
    minHeight: 24,
    paddingTop: 6,
    position: 'absolute',
    right: PAGE_HORIZONTAL_PADDING_PT,
  },
  footerLogo: {
    height: 18,
    objectFit: 'contain',
    objectPosition: 'left center',
    width: 90,
  },
  footerPageNumber: {
    color: '#4b5563',
    fontSize: 7,
  },
});

export default function ExportTablePdf<T>({
  columns,
  headerMeta,
  rows,
  title,
}: ExportTablePdfProps<T>) {
  const columnWeights = columns.map(
    (column) => column.pdfWidth ?? DEFAULT_PDF_COLUMN_WEIGHT,
  );
  const totalWeight =
    columnWeights.reduce((total, weight) => total + weight, 0) ||
    DEFAULT_PDF_COLUMN_WEIGHT;
  const smallestWeight = columnWeights.length
    ? Math.min(...columnWeights)
    : DEFAULT_PDF_COLUMN_WEIGHT;
  // Grow the content area so even the narrowest column clears the legible minimum;
  // for a handful of columns this stays at standard A4 width.
  const requiredContentWidth = (MIN_PDF_COLUMN_WIDTH_PT / smallestWeight) * totalWeight;
  const contentWidth = Math.max(A4_CONTENT_WIDTH_PT, requiredContentWidth);
  const pageWidth = contentWidth + PAGE_HORIZONTAL_PADDING_PT * 2;
  const columnWidths = new Map(
    columns.map((column) => [
      column.id,
      ((column.pdfWidth ?? DEFAULT_PDF_COLUMN_WEIGHT) / totalWeight) * contentWidth,
    ]),
  );

  return (
    <Document>
      <Page size={[pageWidth, A4_LANDSCAPE_HEIGHT_PT]} style={styles.page}>
        <View style={styles.reportHeader} fixed>
          {headerMeta.companyLogoBase64 ? (
            <Image src={headerMeta.companyLogoBase64} style={styles.companyLogo} />
          ) : (
            <Text style={styles.companyName}>{headerMeta.companyName}</Text>
          )}
          <View style={styles.reportHeaderMeta}>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.metaText}>
              {t('export.generated_at', { date: headerMeta.generatedAt })}
            </Text>
            {headerMeta.filterDateLabel && (
              <Text style={styles.metaText}>
                {t('export.filtered_on', { date: headerMeta.filterDateLabel })}
              </Text>
            )}
          </View>
        </View>
        <View style={[styles.tableHeaderRow, { width: contentWidth }]} fixed>
          {columns.map((column) => (
            <Text
              key={column.id}
              style={[styles.th, { width: columnWidths.get(column.id) }]}
            >
              {withSoftBreaks(column.displayHeader ?? column.header)}
            </Text>
          ))}
        </View>
        <View style={[styles.table, { width: contentWidth }]}>
          {rows.map((row, rowIndex) => (
            <View
              key={rowIndex}
              style={[
                styles.tr,
                { width: contentWidth },
                rowIndex % 2 === 1 ? styles.trAlt : {},
              ]}
            >
              {columns.map((column) => (
                <Text
                  key={column.id}
                  style={[styles.td, { width: columnWidths.get(column.id) }]}
                >
                  {withSoftBreaks(column.get(row))}
                </Text>
              ))}
            </View>
          ))}
        </View>
        <View style={styles.footer} fixed>
          {headerMeta.treadLogoBase64 ? (
            <Image src={headerMeta.treadLogoBase64} style={styles.footerLogo} />
          ) : (
            <Text />
          )}
          <Text
            style={styles.footerPageNumber}
            render={({ pageNumber, totalPages }) =>
              t('export.page_of', { page: pageNumber, total: totalPages })
            }
          />
        </View>
      </Page>
    </Document>
  );
}
